/**
 * 全局口径常量 —— **唯一来源**。
 *
 * 为什么需要这个文件：新版设计说明第一章列了 10 项「版本变更与口径校正」
 * （旧口径的成员规模三处不一、预警准确率 89.3%→92.7%、指标 127→187 项…）。
 * 这类数字散落在种子数据、接口文案、前端页面与文档里时，**必然出现同一件事多个值** ——
 * 上一版就已经发生过（成员规模同时存在 3000+ / 700+ / 上限 1000）。
 *
 * 因此这里集中定义，其余位置一律引用；并由 `verify-caliber.ts` 对照
 * `fixtures/caliber-golden.json`（由原型 data.js 直接导出）逐值校验 ——
 * **口径不一致要在自测里失败，而不是靠人工比对文档。**
 *
 * 改动本文件时必须同步重新导出夹具，否则自测会拦下你 —— 这是刻意的摩擦。
 */
/** 术语：全站统一使用「成员」，不再使用「会员」（成员手册源文件名除外）。 */
export const terminology = {
    person: '成员',
    personPlural: '成员',
    deprecated: ['成员'],
    note: '新版设计说明与《成员手册》均统一为「成员」。',
};
/** 三康综合指数（企业家生态健康的评价指数）。 */
export const skCaliber = {
    /** 统称 */
    name: '三康综合指数',
    /** 三个分指数 */
    axes: [
        { key: 'H', name: '生命健康', en: 'Health', weight: 40, sub: '首长保健体系 · 12 类生物医学指标' },
        { key: 'E', name: '企业治理健康', en: 'Enterprise', weight: 30, sub: '8 维企业运营数据' },
        { key: 'W', name: '资产健康', en: 'Assets', weight: 30, sub: '6 类资产参数' },
    ],
    /** 合成公式（展示用） */
    formula: '三康综合指数 = H⁰·⁴⁰ × E⁰·³⁰ × W⁰·³⁰',
    formulaNote: '采用加权几何平均。算术平均下「一维塌陷」会被另外两维掩盖；几何平均会让塌陷直接反映到总分 —— 这是「健康是 1，其余是 0」的数学表达。',
    /** 指标规模：187 项 / 26 维度（12 生物医学 + 8 企业运营 + 6 资产参数） */
    scale: {
        items: 187,
        dims: 26,
        breakdown: [
            { name: '生物医学指标', n: 12, axis: 'H' },
            { name: '企业运营数据', n: 8, axis: 'E' },
            { name: '资产参数', n: 6, axis: 'W' },
        ],
    },
};
/** 生命健康 H。 */
export const healthCaliber = {
    /** 六层指标体系（旧版为 4 层，新版校正为 6 层） */
    layers: ['细胞与分子层', '器官功能层', '循环与代谢层', '免疫与炎症层', '神经与心理层', '行为与睡眠层'],
    /** 风险预警提前期 */
    horizon: '6–12 个月风险预警 · 3–5 年器官趋势预测',
    /** AI 预警准确率（旧口径 89.3%） */
    accuracy: '92.7%',
};
/** 企业治理健康 E。 */
export const enterpriseCaliber = {
    source: '8 维企业运营数据',
    /** 一级指标 5 项 */
    dims: 5,
    /**
     * 二级指标 **19 项**（4+4+4+4+3）。
     *
     * ⚠️ 注意：新版设计说明正文写「18 项」，但同版 data.js 实际定义的是 19 项 ——
     * 与上一版完全相同的自相矛盾，**新版并没有修正它**。
     * 本平台按数据口径取 19，并已列入《设计说明口径更正》要求更正正文。
     */
    subIndicators: 19,
    subPerDim: [4, 4, 4, 4, 3],
};
/** 资产健康 W。 */
export const wealthCaliber = {
    source: '6 类资产参数',
    /** 健康信托档位 */
    trustPlans: 3,
};
/** 平台规模（新版校正后的统一口径）。 */
export const platformScale = [
    { value: '1400', label: '在册成员' },
    { value: '1000+', label: '三甲医院' },
    { value: '10000+', label: '专家资源' },
    { value: '92.7%', label: 'AI 预警准确率' },
];
/** 医疗网络与应急时效。 */
export const serviceCaliber = {
    network: '全国 300+ 城市 · 1000+ 家三甲医院 · 10000+ 名专家',
    /** 应急响应时效（旧口径仅「7×24 响应」） */
    sla: [
        { k: '异常波动触发三级响应', v: '0.3 秒' },
        { k: '健康咨询响应', v: '15 分钟内' },
        { k: '需求确认', v: '2 小时内' },
        { k: '预约反馈', v: '1 个工作日内' },
    ],
    /** 专属守护小组（旧口径为「1 位专属健康管家」） */
    squadSize: '5–8',
    squadBoards: ['健康', '企业', '资产', '商务'],
};
/** 会籍。 */
export const membershipCaliber = {
    type: '终身会籍',
    seats: '主成员 1 名 + 附属成员 2 名（配偶及 21 岁以下子女）',
    transferable: '可继承 · 可转让 · 含私董会合伙人资格',
};
/** 底部导航：**智能体「叁」为第一入口**（取代原来的「首页」）。 */
export const navigationCaliber = [
    { id: 'agent', label: '叁', title: '智能体「叁」' },
    { id: 'sk', label: '三康', title: '三康综合指数' },
    { id: 'report', label: '报告', title: 'AI 报告中心' },
    { id: 'svc', label: '服务', title: '守护服务与权益' },
    { id: 'me', label: '我的', title: '我的' },
];
/**
 * 五组权益与各自条目。
 *
 * 措辞边界：只描述服务内容与次数，不承诺任何健康结果（平台不开展诊疗）。
 */
export const benefitCatalog = [
    {
        key: 'health',
        name: '健康守护',
        items: [
            {
                key: 'expert_clinic',
                name: '三甲专家门诊预约',
                unit: '次',
                quota: 6,
                included: true,
                perYear: true,
                desc: '覆盖合作三甲医院专家号源预约，含挂号与就诊前提醒',
            },
            {
                key: 'admission',
                name: '住院与手术安排协调',
                unit: '次',
                quota: 2,
                included: true,
                perYear: true,
                desc: '住院床位与手术排期协调，由专属守护小组对接医院',
            },
            {
                key: 'mdt',
                name: 'MDT 多学科会诊',
                unit: '次',
                quota: 1,
                included: true,
                perYear: true,
                desc: '多学科专家联合研判并输出整合建议，供本人与主诊医生参考',
            },
            {
                key: 'escort',
                name: '全程陪诊与就医协助',
                unit: '次',
                quota: 4,
                included: true,
                perYear: true,
                desc: '院内陪诊、检查引导与流程协调，含就诊记录归档',
            },
        ],
    },
    {
        key: 'exam',
        name: '检测与干预',
        items: [
            {
                key: 'annual_exam',
                name: '年度深度检测',
                unit: '次',
                quota: 1,
                included: true,
                perYear: true,
                desc: '年度全量指标体系检测，结果用于三康指数与趋势对比',
            },
            {
                key: 'hra',
                name: 'HRA 健康风险评估',
                unit: '次',
                quota: 2,
                included: true,
                perYear: true,
                desc: '无创功能状态评估，约 20 分钟，含结果解读',
            },
            {
                key: 'th_monitor',
                name: 'TH 超微细胞动态监测',
                unit: '次',
                quota: 1,
                included: false,
                perYear: true,
                desc: '增值服务：功能医学方向的动态监测，费用单独约定',
            },
            {
                key: 'rehab',
                name: '深度疗养干预项目',
                unit: '次',
                quota: 1,
                included: false,
                perYear: true,
                desc: '增值服务：疗养基地集中干预，含食宿与随访安排',
            },
        ],
    },
    {
        key: 'enterprise',
        name: '企业治理',
        items: [
            {
                key: 'e_diagnose',
                name: '企业健康度评估',
                unit: '次',
                quota: 2,
                included: true,
                perYear: true,
                desc: '按 E 维 8 类运营数据出具企业健康度评估报告',
            },
            {
                key: 'e_advisor',
                name: '企业发展顾问陪跑',
                unit: '次',
                quota: 4,
                included: true,
                perYear: true,
                desc: '企业发展顾问就经营议题进行一对一顾问式沟通',
            },
            {
                key: 'policy_match',
                name: '政策红利匹配与申报协助',
                unit: '次',
                quota: 4,
                included: true,
                perYear: true,
                desc: '政策匹配清单与申报材料协助，不含审批结果承诺',
            },
            {
                key: 'capital_link',
                name: '产业资本与并购资源对接',
                unit: '次',
                quota: 2,
                included: true,
                perYear: true,
                desc: '按需对接合作机构的资本与并购资源，撮合结果不承诺',
            },
        ],
    },
    {
        key: 'wealth',
        name: '资产与保障',
        items: [
            {
                key: 'trust_review',
                name: '健康支出信托方案评审',
                unit: '次',
                quota: 2,
                included: true,
                perYear: true,
                desc: '健康支出与家族信托结合的架构评审，由持牌机构出具意见',
            },
            {
                key: 'asset_review',
                name: '资产配置年度复盘',
                unit: '次',
                quota: 2,
                included: true,
                perYear: true,
                desc: '结合企业状况与健康趋势的资产配置复盘',
            },
            {
                key: 'succession_plan',
                name: '跨代传承方案评审',
                unit: '次',
                quota: 1,
                included: true,
                perYear: true,
                desc: '传承架构、税务与资产隔离方案的联席评审',
            },
            {
                key: 'tax_legal',
                name: '税务与法务联席咨询',
                unit: '次',
                quota: 4,
                included: true,
                perYear: true,
                desc: '税务与法务持牌机构联合咨询，按次预约',
            },
        ],
    },
    {
        key: 'circle',
        name: '圈层与商务',
        items: [
            {
                key: 'private_board',
                name: '私董会月度会议',
                unit: '次',
                quota: 12,
                included: true,
                perYear: true,
                desc: '每月一次封闭式私董会，议题由成员共同提出',
            },
            {
                key: 'strategy_ride',
                name: '战略顾问陪跑',
                unit: '次',
                quota: 6,
                included: false,
                perYear: true,
                desc: '增值服务：关键决策节点的战略顾问一对一陪跑',
            },
            {
                key: 'global_study',
                name: '全球游学席位',
                unit: '次',
                quota: 1,
                included: false,
                perYear: true,
                desc: '增值服务：年度游学席位，含行程与参访安排',
            },
            {
                key: 'eco_match',
                name: '生态项目对接闭门会',
                unit: '次',
                quota: 4,
                included: true,
                perYear: true,
                desc: '生态内项目与资源的闭门对接，双向选择、不承诺结果',
            },
        ],
    },
];
/** 权益中心（清单来自 `benefitCatalog`，接口与数据库均引用本对象）。 */
export const benefitCaliber = {
    groups: benefitCatalog.length,
    items: benefitCatalog.reduce((n, g) => n + g.items.length, 0),
    resetNote: '当年未使用的基础成员权益不累加至下一年度，到期自动归零，不可结转、不可兑换现金。',
    /** 有效期口径：自然年（当年 1 月 1 日 – 12 月 31 日）。到期日由 core/benefits.ts 按当年算出，不写死年份 */
    periodNote: '权益按自然年计：当年 1 月 1 日至 12 月 31 日有效，次年重新计算。',
    catalog: benefitCatalog,
};
/** AI 报告中心：三类报告。 */
export const reportCaliber = {
    types: [
        { key: 'analysis', name: '综合检测分析报告', question: '现在怎么了？', sla: '数据齐备后 15 分钟内生成' },
        { key: 'alert', name: '预警报告', question: '接下来会怎样、去哪个维度？', sla: '异常触发后 30 分钟内生成' },
        { key: 'plan', name: '干预意见方案', question: '那我该做什么？', sla: '预警确认后 2 小时内生成' },
    ],
    engineTag: '三康螺旋引擎 SK-Engine v1.0 · 多模态风险预警模型 v3.2',
};
/** 技能库接口（智能体调用的能力单元）。 */
export const skillCaliber = [
    'POST /sk/score',
    'POST /sk/simulate',
    'POST /sk/leverage',
    'POST /nlp/report',
    'POST /svc/book',
    'POST /benefit/consume',
    'POST /policy/match',
    'POST /trust/review',
    'POST /sos/trigger',
];
//# sourceMappingURL=caliber.js.map